import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './TaskModal.css';

export interface TaskModalField {
  id: string;
  label: string;
  /** Renders a `<select>` when options is provided, otherwise a text input. */
  options?: string[];
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
  /** `date` / `time` / `datetime-local` → special input type. */
  inputType?: 'date' | 'time' | 'datetime-local' | 'text' | 'number';
  min?: number;
  max?: number;
}

export function TaskModal({
  open,
  title,
  fields,
  children,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  onSubmit,
  onCancel,
}: {
  open: boolean;
  title: string;
  /** Optional inline fields rendered with a generated form. If `children`
   *  is provided, the fields/form are skipped (caller renders its own). */
  fields?: TaskModalField[];
  /** Custom body content. When provided, the modal renders this in
   *  place of the generated field form. */
  children?: ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  onSubmit?: (values: Record<string, string>) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries((fields ?? []).map((f) => [f.id, f.defaultValue ?? ''])),
  );

  useEffect(() => {
    if (open) {
      setValues(Object.fromEntries((fields ?? []).map((f) => [f.id, f.defaultValue ?? ''])));
    }
  }, [open, fields]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className="nx-task-modal__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="nx-task-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="task-modal"
      >
        <h2 className="nx-task-modal__title">{title}</h2>
        {children ? (
          <>{children}</>
        ) : (
          <form
            className="nx-task-modal__form"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit?.(values);
            }}
          >
            {(fields ?? []).map((f) => (
              <label key={f.id} className="nx-task-modal__row">
                <span>{f.label}</span>
                {f.options ? (
                  <select
                    required={f.required}
                    value={values[f.id] ?? ''}
                    onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                    data-testid={`task-modal-${f.id}`}
                  >
                    <option value="" disabled>
                      Select…
                    </option>
                    {f.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.inputType ?? 'text'}
                    required={f.required}
                    value={values[f.id] ?? ''}
                    placeholder={f.placeholder}
                    min={f.min}
                    max={f.max}
                    onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                    data-testid={`task-modal-${f.id}`}
                  />
                )}
              </label>
            ))}
            <div className="nx-task-modal__actions">
              <button type="button" onClick={onCancel} data-testid="task-modal-cancel">
                {cancelLabel}
              </button>
              <button type="submit" className="nx-task-modal__submit" data-testid="task-modal-submit">
                {submitLabel}
              </button>
            </div>
          </form>
        )}
        {children && (
          <div className="nx-task-modal__actions">
            <button type="button" onClick={onCancel} data-testid="task-modal-cancel">
              {cancelLabel}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}